import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, message } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { clinicService } from '../../services';
import { MedicalFacility } from '../../types';
import MedicalFacilityModal from '../../components/MedicalFacilityModal';
import dayjs from 'dayjs';
import './MedicalFacilityManagementPage.css';

const MedicalFacilityManagementPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFacility, setEditingFacility] = useState<MedicalFacility | null>(null);

  const { data: apiResponse, isLoading, error } = useQuery({
    queryKey: ['medical-facilities', currentPage, pageSize],
    queryFn: () => clinicService.getList(currentPage, pageSize),
  });

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  // Build full address from components
  const buildAddress = (facility: MedicalFacility): string => {
    const parts = [facility.street, facility.hamlet, facility.ward_code, facility.province_code].filter(Boolean);
    return parts.join(', ');
  };

  const columns: ColumnsType<MedicalFacility> = [
    {
      title: 'STT',
      key: 'stt',
      width: 60,
      align: 'center',
      render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên đơn vị',
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    {
      title: 'Mã số thuế',
      dataIndex: 'tax_code',
      key: 'tax_code',
      width: 120,
    },
    {
      title: 'Địa chỉ',
      key: 'address',
      width: 250,
      render: (_: any, record: MedicalFacility) => buildAddress(record),
    },
    {
      title: 'Người đại diện',
      dataIndex: 'representative',
      key: 'representative',
      width: 180,
    },
    {
      title: 'Điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Ngày bắt đầu',
      dataIndex: 'start_date',
      key: 'start_date',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Ngày kết thúc',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Tùy chọn',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: any, record: MedicalFacility) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingFacility(record);
              setModalVisible(true);
            }}
          />
          <Button type="link" icon={<EyeOutlined />} />
          <Button type="link" icon={<LinkOutlined />} />
        </Space>
      ),
    },
  ];

  // Show error message when API call fails
  useEffect(() => {
    if (error) {
      message.error('Không thể tải danh sách cơ sở y tế');
    }
  }, [error]);

  // Filter data based on search text
  const filteredData = apiResponse?.data.filter((item) =>
    item.name.toLowerCase().includes(searchText.toLowerCase()) ||
    buildAddress(item).toLowerCase().includes(searchText.toLowerCase()) ||
    item.representative.toLowerCase().includes(searchText.toLowerCase()) ||
    item.tax_code.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  return (
    <div className="medical-facility-management-page">
      <div className="page-header">
        <h1>Quản lý cơ sở y tế</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingFacility(null);
            setModalVisible(true);
          }}
        >
          Tạo mới CSYT
        </Button>
      </div>
      <div className="page-toolbar">
        <Input
          placeholder="Tìm kiếm"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: apiResponse?.meta?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          onShowSizeChange: (current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          },
        }}
        scroll={{ x: 1500 }}
      />
      <MedicalFacilityModal
        visible={modalVisible}
        facility={editingFacility}
        onClose={() => {
          setModalVisible(false);
          setEditingFacility(null);
        }}
      />
    </div>
  );
};

export default MedicalFacilityManagementPage;
